import React, { useLayoutEffect, useRef } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { HierarchyData } from '../../../types';
import { MiniHierarchyMoreRow } from './MiniHierarchyMoreRow';
import { MiniHierarchyRow } from './MiniHierarchyRow';

/** Direct reports shown inline before the "See N more" row takes over. */
const MAX_VISIBLE_REPORTS = 3;
/** Levels of managers shown above the user when they have no direct reports (manager + skip). */
const MAX_MANAGERS_NO_REPORTS = 2;
/** Levels of managers shown above the user when they do have direct reports (immediate manager). */
const MAX_MANAGERS_WITH_REPORTS = 1;

const styles = cssMap({
	scrollArea: {
		flexGrow: 1,
		flexShrink: 1,
		minHeight: 0,
		overflowY: 'auto',
		overflowX: 'hidden',
	},
	connector: {
		width: '3px',
		height: '16px',
		backgroundColor: token('color.background.neutral.hovered'),
		marginBlock: token('space.025'),
	},
	stack: {
		minHeight: '100%',
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

interface MiniHierarchyListProps {
	data: HierarchyData;
	/**
	 * Bumps to force re-centering on the focused user even when the focused person is unchanged - e.g.
	 * each time the reporting-line pane is (re)opened.
	 */
	scrollToken?: number;
	/** Profile-page URL the "See N more" row links to (the full reporting line). */
	moreHref?: string;
	moreTarget?: string;
	/** Fired when the "See N more" row is activated. */
	onMoreClick?: () => void;
	onMoreNavigate?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
	testId?: string;
}

/**
 * Renders a static, read-only reporting line as a single scrollable, top-to-bottom column, capped at
 * three levels:
 * - When the user has direct reports: their immediate manager, the user, then up to
 *   {@link MAX_VISIBLE_REPORTS} direct reports (with a "See N more" row linking to the full reporting
 *   line when there are more).
 * - When the user has no direct reports: up to two levels of managers (immediate manager and
 *   skip-level manager) above the user.
 * */
export function MiniHierarchyList({
	data,
	scrollToken,
	moreHref,
	moreTarget,
	onMoreClick,
	onMoreNavigate,
	testId,
}: MiniHierarchyListProps): React.JSX.Element {
	const { managers = [], user, reports = [] } = data;
	const hasReports = reports.length > 0;

	const maxManagers = hasReports ? MAX_MANAGERS_WITH_REPORTS : MAX_MANAGERS_NO_REPORTS;
	const orderedManagers = managers.slice(0, maxManagers).reverse();

	const visibleReports = reports.slice(0, MAX_VISIBLE_REPORTS);
	const overflowReports = reports.slice(MAX_VISIBLE_REPORTS);
	const showMoreRow = hasReports && overflowReports.length > 0;

	const connector = <Box xcss={styles.connector} aria-hidden testId="hierarchy-connector" />;

	const scrollRef = useRef<HTMLDivElement>(null);
	const selectedRef = useRef<HTMLDivElement>(null);
	useLayoutEffect(() => {
		const container = scrollRef.current;
		const selected = selectedRef.current;
		if (!container || !selected) {
			return;
		}
		const center = () => {
			const target = selected.offsetTop - (container.clientHeight - selected.offsetHeight) / 2;
			container.scrollTop = Math.max(0, target);
		};
		if (container.clientHeight === 0) {
			const frame = requestAnimationFrame(center);
			return () => cancelAnimationFrame(frame);
		}
		center();
	}, [user.accountId, scrollToken]);

	return (
		<Box ref={scrollRef} xcss={styles.scrollArea} testId={testId}>
			<Stack space="space.100" alignBlock="center" alignInline="center" xcss={styles.stack}>
				{orderedManagers.map((manager, index) => (
					<React.Fragment key={manager.accountId}>
						{index > 0 && connector}
						<MiniHierarchyRow
							person={manager}
							testId={`hierarchy-row-manager-${manager.accountId}`}
						/>
					</React.Fragment>
				))}
				{orderedManagers.length > 0 && connector}
				<MiniHierarchyRow
					person={user}
					selected
					rowRef={selectedRef}
					testId="hierarchy-row-selected"
				/>
				{hasReports && connector}
				{visibleReports.map((person) => (
					<MiniHierarchyRow
						key={person.accountId}
						person={person}
						testId={`hierarchy-row-report-${person.accountId}`}
					/>
				))}
				{showMoreRow && (
					<MiniHierarchyMoreRow
						people={overflowReports}
						href={moreHref}
						target={moreTarget}
						onClick={onMoreClick}
						onNavigate={onMoreNavigate}
						testId="hierarchy-row-more"
					/>
				)}
			</Stack>
		</Box>
	);
}
